import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Chip,
  TextField,
  IconButton,
  Typography,
  Tooltip,
  Paper,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Autocomplete,
  createFilterOptions,
  Popover,
} from '@mui/material';
import {
  Label,
  Add,
  Close,
  Check,
  Info,
} from '@mui/icons-material';

const filter = createFilterOptions<string>();

interface ConversationInlineTagEditorProps {
  conversationId: string;
  currentTags: string[];
  onTagsUpdate: (tags: string[]) => Promise<void>;
  availableGlobalTags?: string[];
  compact?: boolean;
}

const ConversationInlineTagEditor: React.FC<ConversationInlineTagEditorProps> = ({
  conversationId,
  currentTags = [],
  onTagsUpdate,
  availableGlobalTags = [],
  compact = true, // Default to compact for conversation rows
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Normalize initial tags to lowercase
  const [tags, setTags] = useState<string[]>(currentTags.map(tag => tag.toLowerCase()));
  const [inputValue, setInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isEditing = Boolean(anchorEl);

  useEffect(() => {
    // Normalize tags to lowercase when they change
    setTags(currentTags.map(tag => tag.toLowerCase()));
  }, [currentTags]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleTagsChange = (event: any, newValue: string[]) => {
    // Filter out any empty strings and duplicates, convert to lowercase
    const cleanedTags = newValue
      .map(tag => tag.trim().toLowerCase().replace(/\s+/g, ''))
      .filter(tag => tag.length > 0);
    const uniqueTags = Array.from(new Set(cleanedTags));
    setTags(uniqueTags);
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    // Convert to lowercase and remove spaces
    const cleanedInput = newInputValue.toLowerCase().replace(/\s+/g, '');
    setInputValue(cleanedInput);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Ensure all tags are lowercase and have no spaces before sending to API
      const cleanedTags = tags.map(tag => tag.trim().toLowerCase().replace(/\s+/g, ''));
      await onTagsUpdate(cleanedTags);
      setAnchorEl(null);
      // Tags will be updated via Redux state, component will re-render automatically
    } catch (error) {
      console.error('Failed to save tags:', error);
      // Revert to original tags on error
      setTags(currentTags);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to lowercase version of current tags
    setTags(currentTags.map(tag => tag.toLowerCase()));
    setInputValue('');
    setAnchorEl(null);
  };

  const handleStartEditing = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  };

  // Get suggestions that aren't already selected (convert to lowercase for consistency)
  const getFilteredOptions = () => {
    return availableGlobalTags
      .map(tag => tag.toLowerCase())
      .filter(tag => !tags.includes(tag));
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {currentTags.length > 0 ? (
          <Tooltip
            title="Click to edit conversation tags"
            placement="top"
          >
            <Box
              onClick={handleStartEditing}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                flexWrap: 'wrap',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <Label sx={{ fontSize: 14, color: 'text.secondary' }} />
              {compact && currentTags.length > 2 ? (
                <>
                  <Typography variant="caption" color="text.secondary">
                    {currentTags.slice(0, 2).join(', ')}
                  </Typography>
                  <Chip
                    label={`+${currentTags.length - 2}`}
                    size="small"
                    sx={{ height: 16, fontSize: '0.65rem' }}
                  />
                </>
              ) : (
                currentTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    icon={<Label sx={{ fontSize: 12 }} />}
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                ))
              )}
            </Box>
          </Tooltip>
        ) : (
          <Tooltip
            title="Add tags to organize this conversation"
            placement="top"
          >
            <Box
              onClick={handleStartEditing}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <Label sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                Add tags
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      <Popover
        open={isEditing}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 320,
            maxWidth: 400,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          },
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        }}
        sx={{
          zIndex: 9999,
        }}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
        }}
      >
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Label sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="medium">
              Edit Tags
            </Typography>
          </Box>

          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={getFilteredOptions()}
            value={tags}
            onChange={handleTagsChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;
              // Suggest the creation of a new value
              const isExisting = options.some((option) => inputValue === option);
              if (inputValue !== '' && !isExisting) {
                filtered.push(inputValue);
              }
              return filtered;
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option}
                  label={option}
                  size="small"
                  icon={<Label sx={{ fontSize: 12 }} />}
                  {...getTagProps({ index })}
                  disabled={isSaving}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                variant="outlined"
                size="small"
                placeholder={tags.length === 0 ? "Type tags (Space to add, Enter to save)" : "Add more (Enter to save)"}
                disabled={isSaving}
                onKeyDown={(e) => {
                  // Handle Enter key to save automatically
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();

                    // If there's input value, add it as a tag first
                    if (inputValue.trim()) {
                      const newTag = inputValue.trim().toLowerCase().replace(/\s+/g, '');
                      if (!tags.includes(newTag) && newTag.length > 0) {
                        const updatedTags = [...tags, newTag];
                        setTags(updatedTags);
                        setInputValue('');
                        // Save with the new tag included
                        setTimeout(() => handleSave(), 0);
                        return;
                      }
                    }

                    // Otherwise just save current tags
                    handleSave();
                    return;
                  }

                  // Prevent capital letters from being typed
                  if (e.key.length === 1 && e.key !== e.key.toLowerCase() && !e.ctrlKey && !e.metaKey) {
                    e.preventDefault();
                    // Insert the lowercase version instead
                    const input = e.target as HTMLInputElement;
                    const start = input.selectionStart || 0;
                    const end = input.selectionEnd || 0;
                    const newValue = inputValue.slice(0, start) + e.key.toLowerCase() + inputValue.slice(end);
                    setInputValue(newValue);
                    // Set cursor position after the inserted character
                    setTimeout(() => {
                      input.setSelectionRange(start + 1, start + 1);
                    }, 0);
                  }

                  // Handle space key to add tag
                  if (e.key === ' ' && inputValue.trim()) {
                    e.preventDefault();
                    e.stopPropagation();
                    const newTag = inputValue.trim().toLowerCase().replace(/\s+/g, '');
                    if (!tags.includes(newTag) && newTag.length > 0) {
                      setTags([...tags, newTag]);
                      setInputValue('');
                    }
                  }

                  // Prevent space from bubbling up
                  if (e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                onClick={(e) => {
                  e.stopPropagation();
                  if (props.onClick) {
                    (props.onClick as any)(e);
                  }
                }}
              >
                <Label sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                {option}
                {!availableGlobalTags.includes(option) && (
                  <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                    (new)
                  </Typography>
                )}
              </Box>
            )}
            disabled={isSaving}
            ChipProps={{
              deleteIcon: <Close sx={{ fontSize: 14 }} />,
            }}
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
              sx={{ fontSize: '0.75rem', py: 0.25 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={12} /> : <Check />}
              sx={{ fontSize: '0.75rem', py: 0.25 }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default ConversationInlineTagEditor;